import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useLexware } from '../context/LexwareContext';

const { FiMenu, FiWifi, FiWifiOff, FiCheckCircle, FiXCircle, FiClock } = FiIcons;

function Header({ toggleSidebar }) {
  const { isConnected, loading, toast, lastAutoBackup } = useLexware();
  const [timeAgo, setTimeAgo] = useState('');

  useEffect(() => {
    if (!lastAutoBackup) {
      setTimeAgo('');
      return;
    }

    const updateTimeAgo = () => {
      const now = new Date();
      const backupTime = new Date(lastAutoBackup);
      const diffInSeconds = Math.floor((now - backupTime) / 1000);
      
      if (diffInSeconds < 60) {
        setTimeAgo('gerade eben');
      } else if (diffInSeconds < 3600) {
        const minutes = Math.floor(diffInSeconds / 60);
        setTimeAgo(`vor ${minutes} Min.`);
      } else if (diffInSeconds < 86400) {
        const hours = Math.floor(diffInSeconds / 3600);
        setTimeAgo(`vor ${hours} Std.`);
      } else {
        const days = Math.floor(diffInSeconds / 86400);
        setTimeAgo(`vor ${days} Tag${days > 1 ? 'en' : ''}`);
      }
    };

    updateTimeAgo();
    // Aktualisiert die relative Zeit jede Minute
    const interval = setInterval(updateTimeAgo, 60000);

    return () => clearInterval(interval);
  }, [lastAutoBackup]);

  // Formatiert die ISO-Zeit zu HH:MM Uhr
  const formatTime = (isoString) => {
    if (!isoString) return '';
    const date = new Date(isoString);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) + ' Uhr';
  };

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="bg-white shadow-sm border-b border-gray-200 px-6 py-4 relative"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={toggleSidebar}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <SafeIcon icon={FiMenu} className="text-gray-600" />
          </button>
          
          <div className="flex items-center gap-2">
            <SafeIcon 
              icon={isConnected ? FiWifi : FiWifiOff} 
              className={`text-sm ${isConnected ? 'text-green-500' : 'text-red-500'}`} 
            />
            <span className={`text-sm font-medium ${isConnected ? 'text-green-600' : 'text-red-600'}`}>
              {isConnected ? 'Online' : 'Offline'}
            </span>

            {/* Anzeige der letzten Sicherung - Jetzt IMMER sichtbar */}
            <div className="flex items-center gap-1.5 ml-2 pl-3 border-l border-gray-300 text-xs text-gray-500">
              <SafeIcon icon={FiClock} className="text-gray-400" />
              {lastAutoBackup ? (
                <>
                  <span>Sicherung: {formatTime(lastAutoBackup)}</span>
                  <span className="text-gray-400">({timeAgo})</span>
                </>
              ) : (
                <span>Noch keine Sicherung</span>
              )}
            </div>

            {loading && (
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin ml-2" />
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center shadow-sm">
            <span className="text-white text-sm font-semibold">
              {/* Fallback auf "L" wenn kein User gefunden wurde */}
              L
            </span>
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: -20, x: '-50%' }}
            className="absolute left-1/2 top-full mt-2 z-50 pointer-events-none"
          >
            <div className={`flex items-center gap-3 px-6 py-3 rounded-xl shadow-2xl border-2 ${
              toast.type === 'success' 
                ? 'bg-green-50 border-green-200 text-green-800' 
                : 'bg-red-50 border-red-200 text-red-800'
            }`}>
              <SafeIcon 
                icon={toast.type === 'success' ? FiCheckCircle : FiXCircle} 
                className="text-xl flex-shrink-0" 
              />
              <span className="font-bold text-sm whitespace-nowrap">{toast.message}</span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}

export default Header;